import { useNavigate } from 'react-router-dom'
import { Sheet, Block, Button } from 'konsta/react'
import { Trophy, UserPlus, ArrowUpDown, Info, Gamepad2, BellOff, X, Clock } from 'lucide-react'
import { formatRelativeTime, cn } from '@/lib/utils'
import { useNotificationStore, type NotificationType } from '@/stores/notificationStore'

const typeConfig: Record<NotificationType, { icon: typeof Trophy; color: string; bg: string }> = {
  jackpot_win: { icon: Trophy, color: '#FF9500', bg: 'bg-[#FF9500]/10' },
  room_invite: { icon: UserPlus, color: '#007AFF', bg: 'bg-[#007AFF]/10' },
  transaction: { icon: ArrowUpDown, color: '#34C759', bg: 'bg-[#34C759]/10' },
  system: { icon: Info, color: '#8E8E93', bg: 'bg-[#8E8E93]/10' },
  game: { icon: Gamepad2, color: '#AF52DE', bg: 'bg-[#AF52DE]/10' },
}

interface NotificationSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function NotificationSheet({ open, onOpenChange }: NotificationSheetProps) {
  const navigate = useNavigate()
  const notifications = useNotificationStore((s) => s.notifications)
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const markAsRead = useNotificationStore((s) => s.markAsRead)
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead)
  const clearAll = useNotificationStore((s) => s.clearAll)

  return (
    <Sheet
      className="pb-safe"
      opened={open}
      onBackdropClick={() => onOpenChange(false)}
      style={{ height: 'auto', maxHeight: '85vh' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <div className="flex items-center gap-2">
          <span className="font-bold text-[17px] text-black">通知</span>
          {unreadCount > 0 && (
            <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-bold flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <button onClick={markAllAsRead} className="text-[13px] text-[#007AFF] font-medium">
              全部已讀
            </button>
          )}
          <button
            onClick={() => onOpenChange(false)}
            className="w-8 h-8 rounded-full bg-black/5 flex items-center justify-center"
          >
            <X className="w-4 h-4 text-[#8E8E93]" />
          </button>
        </div>
      </div>

      <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 160px)' }}>
        {notifications.length === 0 ? (
          <Block className="text-center py-16">
            <BellOff className="w-12 h-12 mx-auto text-[#C7C7CC] mb-3" />
            <p className="text-[#8E8E93] text-[15px] font-medium">沒有通知</p>
            <p className="text-[#C7C7CC] text-[13px] mt-1">新通知將會顯示在這裡</p>
          </Block>
        ) : (
          <div className="px-4 pb-2">
            {notifications.map((n) => {
              const config = typeConfig[n.type]
              const Icon = config.icon
              return (
                <button
                  key={n.id}
                  className={cn(
                    'w-full flex items-start gap-3 p-3 rounded-xl text-left transition-colors mb-1',
                    !n.read ? 'bg-[#007AFF]/[0.04]' : 'active:bg-black/5',
                  )}
                  onClick={() => !n.read && markAsRead(n.id)}
                >
                  {/* Icon */}
                  <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5', config.bg)}>
                    <Icon className="w-5 h-5" style={{ color: config.color }} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={cn(
                        'text-[14px] leading-tight',
                        !n.read ? 'font-semibold text-black' : 'font-medium text-black/70',
                      )}>
                        {n.title}
                      </p>
                      {!n.read && (
                        <span className="w-2 h-2 rounded-full bg-[#007AFF] flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                    <p className="text-[13px] text-[#8E8E93] leading-snug mt-0.5 line-clamp-2">
                      {n.message}
                    </p>
                    <span className="flex items-center gap-1 text-[11px] text-[#C7C7CC] mt-1">
                      <Clock className="w-3 h-3" />
                      {formatRelativeTime(n.timestamp)}
                    </span>
                  </div>
                </button>
              )
            })}
          </div>
        )}
      </div>

      <div className="px-4 pb-4 pt-2 space-y-2 border-t border-black/5">
        <Button
          onClick={() => {
            onOpenChange(false)
            navigate('/notifications')
          }}
        >
          查看全部紀錄
        </Button>
        {notifications.length > 0 && (
          <Button
            clear
            colors={{ textIos: 'text-red-500' }}
            onClick={clearAll}
          >
            清除所有通知
          </Button>
        )}
      </div>
    </Sheet>
  )
}
